import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes, css } from 'styled-components'

const BASE_WIDTH = 1440
const MIN_WIDTH = 860
const MAX_WIDTH = 1440
const MIN_SCALE = 0.8
const MAX_SCALE = 1.15

const backgroundAnimation = keyframes`
  0% {
    background: #0d0d0d;
  }
  100% {
    background: #14183a;
  }
`

interface ScaledWrapperProps {
    children: React.ReactNode
    showBackground?: boolean
}

interface ScaledContainerProps {
    scale: number
    contentHeight?: number
    isLoaded?: boolean
    showBackground?: boolean
}

const ScaledContent = styled.div<{ scale: number; isLoaded?: boolean }>`
    transform: ${({ scale }) => `scale(${scale})`};
    transform-origin: center top;
    width: ${({ scale }) => `${100 / scale}%`};
    max-width: 1254px !important;
    position: absolute;
    left: 50%;
    transform: ${({ scale }) => `translateX(-50.1%) scale(${scale})`};
    opacity: ${({ isLoaded }) => (isLoaded ? 1 : 0)};
    transition: transform 0.5s ease-out, opacity 0.3s ease-in-out, width 0.5s ease-out;
`

const OuterContainer = styled.div<ScaledContainerProps>`
    width: 100%;
    min-height: 2000px;
    height: ${({ contentHeight, scale }) => (contentHeight ? `${contentHeight * scale}px` : 'auto')};
    position: relative;
    overflow: hidden;

    ${({ showBackground, scale }) =>
        showBackground &&
        css`
            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                width: 99.75vw;
                max-width: 2560px;
                height: ${90 * scale}vh;
                z-index: 0;
                border-radius: 0px 0px 48px 48px;
                animation: ${backgroundAnimation} 1.3s ease-in-out forwards;
                transition: opacity 0.3s ease-in-out, height 0.3s ease-in-out;

                @media (max-width: 940px) {
                    display: none;
                }
                @media (min-height: 1080px) or (min-width: 2160px) {
                    display: none;
                }
            }
        `}

    @media (max-width: 940px) {
        min-height: 1000px;
    }
`

const PlaceholderContent = styled.div<{ isLoaded: boolean }>`
    position: absolute;
    width: 100%;
    height: 100%;
    display: ${({ isLoaded }) => (isLoaded ? 'none' : 'block')};
    opacity: ${({ isLoaded }) => (isLoaded ? 0 : 1)};
    transition: opacity 0.3s ease-in-out;
`

export const ScaledWrapper: React.FC<ScaledWrapperProps> = ({ children, showBackground = false }) => {
    const [scaleFactor, setScaleFactor] = useState(1)
    const [contentHeight, setContentHeight] = useState<number | undefined>(undefined)
    const contentRef = useRef<HTMLDivElement>(null)
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        const calculateScaleFactor = () => {
            const width = window.innerWidth

            if (width <= MIN_WIDTH) {
                return 1
            } else if (width >= BASE_WIDTH) {
                if (width >= MAX_WIDTH) {
                    return MAX_SCALE
                }
                return 1 + ((width - BASE_WIDTH) / (MAX_WIDTH - BASE_WIDTH)) * (MAX_SCALE - 1)
            } else {
                return MIN_SCALE + ((width - MIN_WIDTH) / (BASE_WIDTH - MIN_WIDTH)) * (1 - MIN_SCALE)
            }
        }

        const updateScaling = () => {
            const newScale = calculateScaleFactor()
            setScaleFactor(newScale)

            if (contentRef.current) {
                const height = contentRef.current.scrollHeight
                setContentHeight(height)

                setTimeout(() => {
                    setIsLoaded(true)
                }, 100)
            }
        }

        updateScaling()
        window.addEventListener('resize', updateScaling)

        return () => {
            window.removeEventListener('resize', updateScaling)
        }
    }, [])

    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight)
        }
    }, [children])

    return (
        <OuterContainer
            className="scaled-container"
            scale={scaleFactor}
            contentHeight={contentHeight}
            isLoaded={isLoaded}
            showBackground={showBackground}
        >
            <PlaceholderContent isLoaded={isLoaded} />
            <ScaledContent className="scaled-content" ref={contentRef} scale={scaleFactor} isLoaded={isLoaded}>
                {children}
            </ScaledContent>
        </OuterContainer>
    )
}

export default ScaledWrapper
